import React, { useState, useEffect, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useApi } from "./api";
import { useAuth } from "./auth";

const UserInfoContext = createContext({
  roles: [],
  stripeIsSetup: false,
  isAccountFullySetup: false,
  coachSubscription: null,
});

const PLATFORM_OWNER_USER_ID = import.meta.env.VITE_PLATFORM_OWNER_USER_ID;

export const useUserInfo = () => useContext(UserInfoContext);

function getStoredProfile() {
  try {
    const stored = localStorage.getItem("userInfo");
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.error("Error parsing userInfo from localStorage:", e);
    return null;
  }
}

export const UserInfoContextProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(() => getStoredProfile() ?? {});
  const [coachSubscription, setCoachSubscription] = useState(null);

  const {
    getCurrentUser,
    getUser,
    getAccount,
    getCoachSubscription,
    getUserProducts,
  } = useApi();

  useEffect(() => {
    if (!user) {
      setCoachSubscription(null);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setCoachSubscription(null);
      let profile = getStoredProfile();

      if (!profile) {
        profile = await getCurrentUser();
        if (profile.coachId) {
          const coachInfo = await getUser(profile.coachId);
          profile.coachAccountId = coachInfo.stripeAccountId;
        }
        setUserProfile(profile);
        try {
          localStorage.setItem("userInfo", JSON.stringify(profile));
        } catch (e) {
          console.error("Error saving userInfo to localStorage:", e);
        }
      } else {
        setUserProfile(profile);
      }

      if (!profile?.isCoach) {
        const [assigned, purchased] = await Promise.all([
          getUserProducts(profile.id, "assigned").catch(() => []),
          getUserProducts(profile.id, "purchased").catch(() => []),
        ]);
        const hasAssigned = Array.isArray(assigned) && assigned.length > 0;
        const hasPurchased = Array.isArray(purchased) && purchased.length > 0;
        setUserProfile((prev) => ({
          ...prev,
          showPaymentLink:
            hasAssigned && !profile.stripeCustomerId && !hasPurchased,
        }));
        setLoading(false);
        return;
      }

      const [subscriptionRes, connectedAccount] = await Promise.all([
        getCoachSubscription().catch((e) => {
          console.error("Error fetching coach subscription:", e);
          return null;
        }),
        profile?.stripeAccountId
          ? getAccount(profile.stripeAccountId).catch((e) => {
              console.error("Error checking connected Stripe account:", e);
              return null;
            })
          : Promise.resolve(null),
      ]);

      const paymentsProEnabled = !!subscriptionRes?.paymentsPro;
      const isPlatformOwner = profile?.id === PLATFORM_OWNER_USER_ID;

      setCoachSubscription(subscriptionRes);

      if (isPlatformOwner) {
        setUserProfile((prev) => ({
          ...prev,
          stripeIsSetup: true,
          isAccountFullySetup: true,
          paymentsProEnabled,
        }));
      } else if (profile?.stripeAccountId && connectedAccount) {
        const stripeIsSetup =
          connectedAccount.charges_enabled && connectedAccount.payouts_enabled;
        const requirements = connectedAccount?.requirements ?? {};
        const currentlyDue = requirements.currently_due ?? [];
        const pendingVerification = requirements.pending_verification ?? [];
        setUserProfile((prev) => ({
          ...prev,
          stripeIsSetup,
          isAccountFullySetup: stripeIsSetup,
          stripeDetailsSubmitted: !!connectedAccount.details_submitted,
          stripeHasTasksDue: currentlyDue.length > 0,
          stripeCurrentlyDue: currentlyDue,
          stripePendingVerification: pendingVerification,
          stripeDisabledReason: requirements.disabled_reason ?? null,
          paymentsProEnabled,
        }));
      } else {
        setUserProfile((prev) => ({
          ...prev,
          stripeIsSetup: false,
          isAccountFullySetup: false,
          stripeDetailsSubmitted: false,
          stripeHasTasksDue: false,
          stripeCurrentlyDue: [],
          stripePendingVerification: [],
          stripeDisabledReason: null,
          paymentsProEnabled,
        }));
      }
      setLoading(false);
    };

    load();
  }, [user, location?.pathname]);

  return (
    <UserInfoContext.Provider
      value={{ ...userProfile, loading, coachSubscription }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};
